// src/drivers/force-graph-driver.js
//
// Wraps force-graph (vasturiano's `force-graph`, plain canvas, no React) —
// never `import ForceGraph from 'force-graph'` here, same reason as the
// other two drivers. The real default export (a factory of factories:
// `ForceGraph()` returns a constructor, which is then called with the
// container) is injected by whoever builds this driver.
//
// force-graph has no built-in "selection" concept distinct from a click —
// unlike Cytoscape (tap-to-select) and vis-network (click vs selectNode are
// genuinely different events there), there is exactly one node-picking
// event here. Rather than throw on `on('select', ...)` and force every
// caller written against the other two drivers to special-case this one,
// 'select' fires on the same node click as 'click' does — the honest
// reading of "the user picked this node" this engine actually offers.
//
// Behaviours fixed here after a real user tried the demo and found it
// unusable, not from reading force-graph's docs — verified against its own
// source, not guessed:
//
// 1. **A fresh `graphData()` call renders before the layout has spread
//    out.** force-graph's simulation is continuous (d3-force, ticked via
//    rAF) and `fit()`/`zoomToFit()` is a one-shot call, not something the
//    engine keeps doing on its own — calling it right after `setData()`
//    (exactly what this library's own demo does on every dropdown change)
//    was zooming onto the nodes' tiny initial cluster near the origin,
//    before physics had moved them anywhere. Cytoscape's own `cose` also
//    animates continuously by default (verified the hard way — see this
//    library's own CytoscapeDriver.destroy(), a real bug from trusting the
//    opposite) yet never shows this problem, because cytoscape's real
//    layouts default to `fit: true` *themselves* — the layout keeps
//    re-fitting the viewport on every one of its own animation frames,
//    independent of whether anything outside it calls `fit()` at all.
//    force-graph has no such self-fitting behaviour, so this driver has to
//    manufacture the same effect another way: `warmupTicks` — "how many
//    times to tick the force engine before starting to render", confirmed
//    in force-graph's own source to run synchronously inside `graphData()`
//    itself, every time it is called, not only at construction — settles
//    the layout enough, before this driver's own `fit()` is ever called,
//    that a one-shot `zoomToFit()` lands on something real.
// 2. **Touching any node made the whole graph scatter, with no console
//    error — the physics itself, working as designed, not a bug in this
//    driver's event wiring.** force-graph's own drag start deliberately
//    re-heats the whole simulation (`d3AlphaTarget(0.3)`, "keep engine
//    running at low intensity throughout drag" in its own source) so a
//    dragged node's neighbours visibly adjust to it — a deliberate "alive"
//    feel that reads as "the graph fights me" on ~220 mostly-unrelated
//    nodes clustered around one hub, and un-pins a released node back into
//    that same live simulation by default, so even letting go did not
//    hold a position. Cytoscape and vis-network never fight a manual
//    reposition this way because neither keeps its physics running once
//    its own layout settles. Fixed by giving force-graph the same
//    property: every node is pinned (`fx`/`fy`) the instant `setData()`'s
//    warmup settles it, turning the simulation from "always live" into
//    "computes a layout once, then holds still" — matching what this
//    demo actually wants from all three engines, a comparable static map,
//    not three different amounts of ongoing jiggle. A drag still works
//    exactly as before (force-graph's own drag machinery moves a pinned
//    node's `fx`/`fy` the same way as an unpinned one); it now simply
//    *stays* moved, like the other two drivers, instead of springing back
//    or dragging its neighbours into a scramble.

import { GraphDriver } from '../graph-driver.js';

export class ForceGraphDriver extends GraphDriver {
    /** @param {() => object} force_graph_factory the `ForceGraph` function itself (called as `ForceGraph()(container)`) */
    constructor(force_graph_factory) {
        super();

        if (typeof force_graph_factory !== 'function') {
            throw new Error('ForceGraphDriver needs the real ForceGraph factory function — none was given');
        }

        this._force_graph_factory = force_graph_factory;
        this._fg = null;
        this._listeners = { click: [], hover: [], select: [], drag: [] };
    }

    mount(container, options = {}) {
        this._container = container;
        this._fg = this._force_graph_factory()(container);

        this._fg
            .width(container.clientWidth || options.width || 600)
            .height(container.clientHeight || options.height || 400)
            .warmupTicks(options.warmup_ticks_n ?? 100)
            .nodeLabel(node => node.label ?? String(node.id))
            .onNodeClick(node => {
                this._emit('click', { type: 'node', id: String(node.id), data: node });
                this._emit('select', { type: 'node', id: String(node.id), data: node }); // see this file's own header comment
            })
            .onNodeHover(node => {
                if (node !== null) {
                    this._emit('hover', { type: 'node', id: String(node.id), data: node });
                }
            })
            .onNodeDrag((node, translate) => {
                // force-graph's own `translate` is already the delta since
                // the previous tick, not since the drag started (verified
                // against its source, not assumed — the same subtlety
                // CytoscapeDriver's own tests exist to pin down).
                this._emit('drag', { type: 'node', id: String(node.id), data: node, position: { x: node.x, y: node.y }, delta: { dx: translate.x, dy: translate.y } });
            })
            .onNodeDragEnd(node => {
                // Every node is already pinned by the time a drag can even
                // happen (setData(), below, pins everything once the
                // warmup settles) — this line is only a safety net for a
                // node that somehow reached a drag unpinned, keeping this
                // driver's contract ("a drop stays where it was dropped")
                // true regardless of how a future caller ends up here.
                node.fx = node.x;
                node.fy = node.y;
            });

        // An escape hatch, not a lowest-common-denominator options object:
        // force-graph's own API is a long chain of setters this driver has
        // no reason to wrap one-by-one just so a caller can set a link
        // color or a particle count.
        if (typeof options.configure === 'function') {
            options.configure(this._fg);
        }
    }

    setData(nodes, edges) {
        this._fg.graphData({
            nodes: nodes.map(node => ({ ...node })),
            links: edges.map(edge => ({ ...edge })), // force-graph's own field is `links`; `source`/`target` already match this driver's contract
        });

        // Pin every node the instant the warmup above has settled it — see
        // this file's own header comment (fix 2) for why: this driver
        // computes a layout once, the same as the other two, rather than
        // leaving force-graph's physics live and fighting every future
        // interaction with it.
        for (const node of this._fg.graphData().nodes) {
            node.fx = node.x;
            node.fy = node.y;
        }
    }

    on(event, callback) {
        if (!(event in this._listeners)) {
            throw new Error(`ForceGraphDriver has no "${event}" event — use click, hover, select, or drag`);
        }

        this._listeners[event].push(callback);
    }

    _emit(event, payload) {
        for (const callback of this._listeners[event]) {
            callback(payload);
        }
    }

    moveNode(id, dx, dy) {
        const node = this._fg.graphData().nodes.find(n => String(n.id) === String(id));

        if (node === undefined) {
            return;
        }

        // Every node is already pinned (see setData()) — this just moves
        // that pin by a relative delta, same as the other two drivers'
        // moveNode(). x/y are updated too so a caller reading the node
        // back immediately (rather than waiting a frame) sees the move.
        node.fx = (node.fx ?? node.x) + dx;
        node.fy = (node.fy ?? node.y) + dy;
        node.x = node.fx;
        node.y = node.fy;
    }

    fit() {
        this._fg.zoomToFit(0); // 0ms — instant, not animated, to match the other two drivers' synchronous fit()
    }

    resize() {
        this._fg.width(this._container.clientWidth).height(this._container.clientHeight);
    }

    destroy() {
        if (typeof this._fg._destructor === 'function') {
            this._fg._destructor();
        }

        this._fg = null;
    }
}
